import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
    Upload, 
    CheckCircle2,
    FileText,
    Shield,
    Link as LinkIcon,
    File
} from 'lucide-react';

const RESOURCE_CATEGORIES = [
    "Reports",
    "Articles",
    "Videos",
    "Research",
    "Toolkits",
    "Templates",
    "Guides",
    "Presentations",
    "Events",
    "Other"
];

const AddResourcePage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, login } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [resourceType, setResourceType] = useState('link'); // 'link' or 'file'
    const [uploadProgress, setUploadProgress] = useState(0);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        link: '',
        file: null,
        fileName: '',
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Max 10MB
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File too large. Maximum size is 10MB.');
                return;
            }
            setFormData(prev => ({ 
                ...prev, 
                file: file,
                fileName: file.name 
            }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.category) newErrors.category = 'Category is required';
        
        if (resourceType === 'link') {
            if (!formData.link.trim()) newErrors.link = 'Link is required';
            else if (!/^https?:\/\/.+/.test(formData.link)) {
                newErrors.link = 'Please enter a valid URL starting with http:// or https://';
            }
        } else {
            if (!formData.file) newErrors.file = 'Please select a file to upload';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) return;
        
        setSubmitting(true);
        setUploadProgress(10);
        
        try {
            let resourceUrl = formData.link;
            let fileUrl = '';
            
            // If uploading a file, use the upload endpoint
            if (resourceType === 'file' && formData.file) {
                setUploadProgress(30);
                
                const uploadFormData = new FormData();
                uploadFormData.append('file', formData.file);
                
                const uploadResponse = await api.post('/upload', uploadFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                
                setUploadProgress(60);
                fileUrl = uploadResponse.data.url;
                resourceUrl = '';
            }
            
            setUploadProgress(80);
            
            // Create the news resource entry
            const resourceData = {
                title: formData.title,
                description: formData.description,
                category: formData.category,
                source: user?.name || 'Member Contribution',
                resource_type: resourceType === 'file' ? 'upload' : 'link',
                url: resourceUrl || '',
                file_url: fileUrl || '',
                is_published: true,
            };
            
            await api.post('/news/contribute', resourceData);
            
            setUploadProgress(100);
            setSubmitted(true);
            toast.success('Resource added successfully!');
        } catch (error) {
            console.error('Submission error:', error);
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to add resource. Please try again.');
        } finally {
            setSubmitting(false);
            setUploadProgress(0);
        }
    };

    // Login required
    if (!isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="login-required">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <Shield className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Member Access Required</CardTitle>
                        <CardDescription>
                            Please log in to add resources to the HAAPE Hub.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={login} className="w-full" data-testid="login-btn">
                            Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="submission-success">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold mb-2">Resource Submitted!</h2>
                        <p className="text-muted-foreground mb-6">
                            Thank you for contributing! Your resource has been submitted for review 
                            and will appear on the Resource Library page once approved.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button onClick={() => navigate('/news')} data-testid="go-news-btn">
                                View Resources
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => { 
                                    setSubmitted(false); 
                                    setFormData({
                                        title: '', description: '', category: '', link: '', file: null, fileName: ''
                                    });
                                    setResourceType('link');
                                }}
                                data-testid="add-another-btn"
                            >
                                Add Another
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="add-resource-page">
            <div className="container max-w-2xl mx-auto py-8 px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                        <Upload className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Add a Resource</h1>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Share helpful resources with the HAAPE community. Upload documents, 
                        link to articles, videos, or any content that supports our mission.
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <FileText className="h-5 w-5 text-primary" />
                                Resource Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Resource Title *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="e.g., Guide to Workplace Accommodations"
                                    data-testid="input-title"
                                />
                                {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Brief description of this resource..."
                                    rows={3}
                                    data-testid="input-description"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="category">Category *</Label>
                                <Select 
                                    value={formData.category} 
                                    onValueChange={(v) => handleChange('category', v)}
                                >
                                    <SelectTrigger data-testid="select-category">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RESOURCE_CATEGORIES.map((cat) => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg">Resource Type</CardTitle>
                            <CardDescription>Choose how you want to add the resource</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Resource type selector */}
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    type="button"
                                    onClick={() => setResourceType('link')}
                                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                        resourceType === 'link' 
                                            ? 'border-primary bg-primary/5' 
                                            : 'border-border hover:border-primary/50'
                                    }`}
                                    data-testid="type-link"
                                >
                                    <LinkIcon className={`h-6 w-6 mb-2 ${resourceType === 'link' ? 'text-primary' : 'text-muted-foreground'}`} />
                                    <div className="font-medium">External Link</div>
                                    <div className="text-sm text-muted-foreground">Link to an article, video, or website</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setResourceType('file')}
                                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                        resourceType === 'file' 
                                            ? 'border-primary bg-primary/5' 
                                            : 'border-border hover:border-primary/50'
                                    }`}
                                    data-testid="type-file"
                                >
                                    <File className={`h-6 w-6 mb-2 ${resourceType === 'file' ? 'text-primary' : 'text-muted-foreground'}`} />
                                    <div className="font-medium">Upload File</div>
                                    <div className="text-sm text-muted-foreground">PDF, document, or other file (max 10MB)</div>
                                </button>
                            </div>

                            {/* Link input */}
                            {resourceType === 'link' && (
                                <div className="space-y-2">
                                    <Label htmlFor="link">Resource URL *</Label>
                                    <Input
                                        id="link"
                                        value={formData.link}
                                        onChange={(e) => handleChange('link', e.target.value)}
                                        placeholder="https://example.com/resource"
                                        data-testid="input-link"
                                    />
                                    {errors.link && <p className="text-sm text-destructive">{errors.link}</p>}
                                </div>
                            )}

                            {/* File upload */}
                            {resourceType === 'file' && (
                                <div className="space-y-2">
                                    <Label htmlFor="file">Upload File *</Label>
                                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                                        <input
                                            id="file"
                                            type="file"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif"
                                            data-testid="input-file"
                                        />
                                        <label htmlFor="file" className="cursor-pointer">
                                            {formData.fileName ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <File className="h-8 w-8 text-primary" />
                                                    <span className="font-medium">{formData.fileName}</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                                    <p className="text-sm text-muted-foreground">
                                                        Click to select a file or drag and drop
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        PDF, Word, PowerPoint, Excel, Images (max 10MB)
                                                    </p>
                                                </>
                                            )}
                                        </label>
                                    </div>
                                    {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
                                </div>
                            )}

                            {/* Upload progress */}
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                        className="bg-primary h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="flex justify-end">
                        <Button type="submit" disabled={submitting} className="gap-2" data-testid="submit-btn">
                            {submitting ? (
                                <>Uploading...</>
                            ) : (
                                <>
                                    Submit Resource
                                    <Upload className="h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddResourcePage;
